import { useCallback, useEffect, useRef, useState } from "react";
import { useT } from "../i18n";
import { useDismiss } from "./useDismiss";

/**
 * ConfirmDelete arms a destructive action before it fires.
 *
 * Inline two-step rather than `window.confirm`: the native dialog is unstyled,
 * is suppressed outright in some embedded contexts, and blocks the main thread
 * — which on a phone means the tap appears to do nothing for a beat. Inline
 * also lets the question name what is actually lost, which "Are you sure?"
 * never does.
 *
 * Both deletes behind this are soft on the server, but nothing in the interface
 * can bring a row back, so from where the user stands it is permanent. The
 * confirmation is the only guard there is.
 */
export default function ConfirmDelete({
  label,
  question,
  onConfirm,
  testId,
  compact = false,
  disabled = false,
}: {
  /** What the trigger says, and the accessible name in compact mode. */
  label: string;
  /** Names the consequence. Shown in place of the trigger once armed. */
  question: string;
  onConfirm: () => void;
  testId: string;
  /** Icon-sized trigger, for a row that has no space for a word. */
  compact?: boolean;
  disabled?: boolean;
}) {
  const [armed, setArmed] = useState(false);
  const t = useT();
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const wrapper = useRef<HTMLSpanElement | null>(null);
  const disarm = useCallback(() => setArmed(false), []);

  // Focus lands on the confirm button so the keyboard path is Enter-then-Enter,
  // and so a screen reader announces the question rather than silently swapping
  // the control underneath it.
  useEffect(() => {
    if (armed) confirmRef.current?.focus();
  }, [armed]);

  // Touching anything else is a decision not to delete. Without this an armed
  // button stays armed behind whatever the user does next, and the confirm is
  // waiting under their thumb the next time they come back to the row.
  useDismiss(armed, wrapper, disarm);

  if (!armed) {
    return (
      <button
        type="button"
        aria-label={compact ? label : undefined}
        title={compact ? label : undefined}
        data-testid={testId}
        disabled={disabled}
        onClick={() => setArmed(true)}
        className={
          compact
            ? "rounded-control border border-line px-2 py-1 text-xs transition-colors hover:border-red-400 hover:text-red-600 disabled:opacity-30 dark:hover:text-red-400"
            : "rounded-control border border-line px-3 py-1.5 text-sm transition-colors hover:border-red-400 hover:text-red-600 disabled:opacity-40 dark:hover:text-red-400"
        }
      >
        {compact ? "×" : label}
      </button>
    );
  }

  return (
    <span
      ref={wrapper}
      role="group"
      aria-label={label}
      className="flex flex-wrap items-center gap-2 rounded-control border border-red-300 bg-red-50 px-2 py-1 dark:border-red-900/70 dark:bg-red-950/40"
    >
      <span className="text-xs text-red-800 dark:text-red-200">{question}</span>
      <button
        type="button"
        ref={confirmRef}
        data-testid={`${testId}-confirm`}
        onClick={() => {
          setArmed(false);
          onConfirm();
        }}
        onKeyDown={(e) => {
          if (e.key === "Escape") setArmed(false);
        }}
        className="rounded-control bg-red-600 px-2.5 py-1 text-xs font-medium text-white transition-colors hover:bg-red-700"
      >
        {t.common.delete}
      </button>
      <button
        type="button"
        data-testid={`${testId}-cancel`}
        onClick={() => setArmed(false)}
        onKeyDown={(e) => {
          if (e.key === "Escape") setArmed(false);
        }}
        className="rounded-control px-2 py-1 text-xs text-ink-soft transition-colors hover:bg-sunk"
      >
        {t.common.keep}
      </button>
    </span>
  );
}
